import { toFullName } from '@repo/toolkit/to-full-name'
import DateStringInput from '@repo/ui/date-string-input'
import MultiSelectInput from '@repo/ui/multiselect-input'
import RadioInput from '@repo/ui/radio-input'
import SelectInput from '@repo/ui/select-input'
import Typography from '@repo/ui/typography'
import { useQuery } from '@tanstack/react-query'

import { getTripBookings } from '../../../api/trip-bookings/get-trip-bookings'
import { getTrips } from '../../../api/trips/get-trips'
import { getUsers } from '../../../api/user/get-users'
import { tripStepTypeMapping, TripTypeEnum } from '../../../lib/types/enums/trip/trip-type.enum'
import { TripStepTypeEnum } from '../../../lib/types/enums/trip-bookings/trip-step-type.enum'
import { RoleEnum } from '../../../lib/types/enums/users/role.enum'
import { TripProperties } from '../../../lib/types/models/trip/properties.trip'
import { TripBookingProperties } from '../../../lib/types/models/trip-booking/properties.trip-booking'
import { TripBookingTypes } from '../../../lib/types/models/trip-booking/trip-booking'
import { TripDateFilter } from '../../../lib/types/models/trip-booking/trip-date'
import { tripGapLabel, tripGapOptions } from './trip-date.utils'

type Props = {
  type: 'full' | 'light'
  tripType?: TripTypeEnum
  filter?: TripDateFilter
  onFilterChange: (filter: TripDateFilter) => void
} & ({ type: 'full'; tripType: TripTypeEnum } | { type: 'light' })

export function TripDatesFilter({ filter, type, tripType, onFilterChange }: Props) {
  function setFilter<K extends keyof TripDateFilter>(key: K, value: TripDateFilter[K]) {
    onFilterChange({ ...filter, [key]: value })
  }

  const { data: trips } = useQuery({
    queryKey: ['trips'],
    queryFn: () => getTrips()
  })

  const { data: tripBookings } = useQuery({
    queryKey: ['trip-bookings'],
    queryFn: () => getTripBookings(),
    enabled: type === 'full'
  })

  const { data: users } = useQuery({
    queryKey: ['users'],
    queryFn: () => getUsers({ filter: { roles: [RoleEnum.ADMIN] } }),
    enabled: type === 'full'
  })

  return (
    <div className={'flex flex-col gap-4 mt-2'}>
      <DateStringInput
        label={'Date'}
        value={filter?.date}
        onChange={(date) => setFilter('date', date)}
      />

      <SelectInput
        label={'Type de trajet'}
        options={Object.values(TripStepTypeEnum).map((type) => ({
          label: tripStepTypeMapping[type].label,
          value: type
        }))}
        selectedOption={
          filter?.directionality
            ? {
                label: tripStepTypeMapping[filter.directionality].label,
                value: filter.directionality
              }
            : undefined
        }
        onChange={(option) => setFilter('directionality', option)}
      />

      <MultiSelectInput
        searchable
        label={'Voyages'}
        variant={'primary'}
        options={
          trips?.data.map((trip) => ({
            label: TripProperties.getTripLabel(trip),
            value: trip.id
          })) ?? []
        }
        selectedOptions={filter?.tripIds?.map((tripId) => {
          const trip = trips?.data.find((trip) => trip.id === tripId)
          return {
            label: TripProperties.getTripLabel(trip),
            value: tripId
          }
        })}
        onChange={(tripIds) => setFilter('tripIds', tripIds)}
      />

      {type === 'light' && (
        <div className={'flex flex-col gap-2'}>
          <Typography variant={'label-semibold'}>Places à répartir</Typography>

          <div className="flex flex-row gap-5">
            <RadioInput
              label={'Tous'}
              checked={filter?.isCompleted === undefined}
              onCheck={() => setFilter('isCompleted', undefined)}
            />

            <RadioInput
              label={'Oui'}
              checked={filter?.isCompleted === false}
              onCheck={() => setFilter('isCompleted', false)}
            />

            <RadioInput
              label={'Non'}
              checked={filter?.isCompleted === true}
              onCheck={() => setFilter('isCompleted', true)}
            />
          </div>
        </div>
      )}

      {type === 'full' && (
        <>
          <MultiSelectInput
            type={'number'}
            label={'Réservées'}
            variant={'grey'}
            onChange={(value) => {
              setFilter(
                'numberOfPlacesReserved',
                value.map((val) => Number(val))
              )
            }}
            selectedOptions={
              filter?.numberOfPlacesReserved?.map((duration) => ({
                label: duration.toString(),
                value: duration
              })) ?? []
            }
          />

          <MultiSelectInput
            type={'number'}
            label={tripType === TripTypeEnum.PRIMARY ? 'Demandées #' : 'Dispatchées #'}
            variant={'grey'}
            onChange={(value) => {
              setFilter(
                'numberOfPlacesRequested',
                value.map((val) => Number(val))
              )
            }}
            selectedOptions={
              filter?.numberOfPlacesRequested?.map((duration) => ({
                label: duration.toString(),
                value: duration
              })) ?? []
            }
          />

          <MultiSelectInput
            searchable
            label={'Réservations'}
            variant={'grey'}
            options={tripBookings?.data.map((tripBooking) => ({
              label: TripBookingProperties.getLabel(tripBooking),
              value: tripBooking.id
            }))}
            selectedOptions={filter?.reservationIds?.map((tripBookingId) => ({
              label: TripBookingProperties.getLabel(
                tripBookings?.data.find((rs) => rs.id === tripBookingId) ??
                  ({} as TripBookingTypes.Light)
              ),
              value: tripBookingId
            }))}
            onChange={(tripBookingIds) => setFilter('reservationIds', tripBookingIds)}
          />

          <SelectInput
            label={'Responsable résa'}
            options={users?.data.map((user) => ({
              label: toFullName(user.firstName, user.lastName),
              value: user.id
            }))}
            selectedOption={
              filter?.lecResponsableId
                ? {
                    label: toFullName(
                      users?.data.find((user) => user.id === filter.lecResponsableId)?.firstName,
                      users?.data.find((user) => user.id === filter.lecResponsableId)?.lastName
                    ),
                    value: filter.lecResponsableId
                  }
                : undefined
            }
            onChange={(option) => setFilter('lecResponsableId', option)}
          />

          <SelectInput<boolean>
            label={'A un écart chez les étudiants'}
            options={tripGapOptions}
            selectedOption={
              filter?.remainingStudentPlaces !== undefined
                ? {
                    label: tripGapLabel(filter?.remainingStudentPlaces),
                    value: filter?.remainingStudentPlaces
                  }
                : undefined
            }
            onChange={(option) => setFilter('remainingStudentPlaces', option)}
          />

          <div className={'flex flex-col gap-2'}>
            <Typography variant={'label-semibold'}>Pax à dispatcher</Typography>

            <div className="flex flex-row gap-5">
              <RadioInput
                label={'Tous'}
                checked={filter?.isFullyDispatched === undefined}
                onCheck={() => setFilter('isFullyDispatched', undefined)}
              />

              <RadioInput
                label={'Oui'}
                checked={filter?.isFullyDispatched === false}
                onCheck={() => setFilter('isFullyDispatched', false)}
              />

              <RadioInput
                label={'Non'}
                checked={filter?.isFullyDispatched === true}
                onCheck={() => setFilter('isFullyDispatched', true)}
              />
            </div>
          </div>

          <SelectInput<boolean>
            label={'A un écart chez les encadrants'}
            options={tripGapOptions}
            selectedOption={
              filter?.remainingStaffPlaces !== undefined
                ? {
                    label: tripGapLabel(filter?.remainingStaffPlaces),
                    value: filter?.remainingStaffPlaces
                  }
                : undefined
            }
            onChange={(option) => setFilter('remainingStaffPlaces', option)}
          />
        </>
      )}
    </div>
  )
}
